import {naturalSortByKey} from '@benchling/js-utils';
import {applyEdgeChanges, applyNodeChanges, type Edge, EdgeChange, type Node, NodeChange} from 'reactflow';
import {ActorRefFrom, assertEvent, assign, fromPromise, setup} from 'xstate';

import {LayoutSettings} from 'ui-library/reactflow/layout/calculateDimensionsForLayout';
import layoutFlowchart from 'ui-library/reactflow/layout/layoutFlowchart';

import {EntityMap_entityMap_EntityMapNode_entities as EntityMapEntityNodeOrInaccessibleInfoNode} from './__generated/EntityMap';
import {
  DEFAULT_LAYOUT_DEBOUNCE_MS,
  ENTITY_MAP_TOTAL_NODE_LIMIT,
  INITIAL_DEPTH_ON_LOAD,
} from './EntityMapConstants';
import {fetchEntityMap} from './EntityMapDAO';
import {
  EntityMapGraph,
  extractFullyExpandedEntityIds,
  mergeGraphNodesById,
  parseEntityMapIntoEntityMapGraph,
} from './EntityMapUtils';
import {EntityNodeMachine} from './EntityNodeMachine';

/**
 * Merges two maps, preserving (by key) the values from the first map.
 */
const mergeMapsPreservingExisting = <K, V>(originalMap: Map<K, V>, newMap: Map<K, V>): Map<K, V> =>
  new Map([...newMap.entries(), ...originalMap.entries()]);

interface LayoutGraphInput {
  graphNodesById: Map<string, Node<EntityMapEntityNodeOrInaccessibleInfoNode>>;
  graphEdgesById: Map<string, Edge>;
  layoutSettings: Partial<LayoutSettings<Node<EntityMapEntityNodeOrInaccessibleInfoNode>, Edge>>;
}

interface LayoutGraphOutput {
  graphNodesById: Map<string, Node<EntityMapEntityNodeOrInaccessibleInfoNode>>;
  graphEdgesById: Map<string, Edge>;
}

interface EntityMapInput {
  entityId: string;
  layoutSettings: Partial<LayoutSettings<Node<EntityMapEntityNodeOrInaccessibleInfoNode>, Edge>>;
}

interface EntityMapContext extends EntityMapGraph {
  entityId: string;
  layoutSettings: Partial<LayoutSettings<Node<EntityMapEntityNodeOrInaccessibleInfoNode>, Edge>>;
  spawnedNodeMachines: Map<string, ActorRefFrom<typeof EntityNodeMachine>>;
  fullyExpandedEntityIds: Set<string>;
  isEntityMapSizeOverProductLimit: boolean;
}

interface CompletedFetchingExtendedGraphEvent extends EntityMapGraph {
  type: 'completedFetchingExtendedGraph';
}

type EntityMapEvents =
  | {
      type: 'onNodesChange';
      changes: Array<NodeChange>;
    }
  | {
      type: 'onEdgesChange';
      changes: Array<EdgeChange>;
    }
  | CompletedFetchingExtendedGraphEvent;

/**
 * XState state machine for managing the entity map graph for a given entityId.
 * Handles idle, initial loading, and error states.
 * Spawns child entity node machines for each entity in the graph, and stores them in context keyed by its
 * entityId. Listens for completedFetchingExtendedGraph events, which are sent from the child entity node
 * machines when they have loaded their relationships.
 * Stores graphNodesById and graphEdgesById in context.
 *
 * The idle state handles onNodesChange and onEdgesChange events, which can be sent from the view using
 * actorRef.send().
 */
export const EntityMapMachine = setup({
  types: {
    input: {} as EntityMapInput,
    context: {} as EntityMapContext,
    events: {} as EntityMapEvents,
  },
  actors: {
    loadInitialGraph: fromPromise<EntityMapGraph, {entityId: string}>(async ({input: {entityId}}) => {
      const entityMap = await fetchEntityMap(entityId, INITIAL_DEPTH_ON_LOAD);
      return parseEntityMapIntoEntityMapGraph(entityMap);
    }),
    layoutGraph: fromPromise<LayoutGraphOutput, LayoutGraphInput>(
      async ({input: {graphNodesById, graphEdgesById, layoutSettings}}) => {
        const nodes = naturalSortByKey(Array.from(graphNodesById.values()), 'id');
        const edges = naturalSortByKey(Array.from(graphEdgesById.values()), 'id');

        const {nodes: layoutNodes, edges: layoutEdges} = await layoutFlowchart(
          {nodes, edges},
          layoutSettings
        );

        return {
          graphNodesById: new Map(layoutNodes.map((node) => [node.id, node])),
          graphEdgesById: new Map(layoutEdges.map((edge) => [edge.id, edge])),
        };
      }
    ),
  },
  actions: {
    mergeGraph: assign(({spawn, context}, params: EntityMapGraph) => {
      // When we merge the graphs, we want to preserve the existing nodes and edges, and only add the new
      // ones. This is to ensure that the existing nodes' calculated dimensions are not lost, as they will not
      // be recalculated by reactflow, which will lead to things jumping around or overlapping.
      const graphNodesById = mergeGraphNodesById(context.graphNodesById, params.graphNodesById);
      const graphEdgesById = mergeMapsPreservingExisting(context.graphEdgesById, params.graphEdgesById);

      const fullyExpandedEntityIds = extractFullyExpandedEntityIds({graphNodesById, graphEdgesById});

      const spawnedNodeMachines = new Map(context.spawnedNodeMachines);
      const newEntityIds = Array.from(params.graphNodesById.keys()).filter(
        (entityId) => !spawnedNodeMachines.has(entityId)
      );
      for (const entityId of newEntityIds) {
        const entity = graphNodesById.get(entityId)!.data;
        const entityNodeMachine = spawn(EntityNodeMachine, {
          input: {
            entityId,
            entity,
            mainEntityId: context.entityId,
          },
        });
        spawnedNodeMachines.set(entityId, entityNodeMachine);
      }

      const isEntityMapSizeOverProductLimit = graphNodesById.size >= ENTITY_MAP_TOTAL_NODE_LIMIT;

      return {
        graphNodesById,
        graphEdgesById,
        fullyExpandedEntityIds,
        spawnedNodeMachines,
        isEntityMapSizeOverProductLimit,
      };
    }),
    onNodesChange: assign(({context, event}) => {
      assertEvent(event, 'onNodesChange');
      const nodes: Array<Node> = [...context.graphNodesById.values()];
      const updatedNodes = applyNodeChanges(event.changes, nodes);
      return {
        graphNodesById: new Map(updatedNodes.map((node) => [node.id, node])),
      };
    }),
    onEdgesChange: assign(({context, event}) => {
      assertEvent(event, 'onEdgesChange');
      const edges: Array<Edge> = [...context.graphEdgesById.values()];
      const updatedEdges = applyEdgeChanges(event.changes, edges);
      return {
        graphEdgesById: new Map(updatedEdges.map((edge) => [edge.id, edge])),
      };
    }),
  },
}).createMachine({
  id: 'entityMapMachine',
  context: ({input}) => ({
    entityId: input.entityId,
    layoutSettings: input.layoutSettings,
    graphNodesById: new Map(),
    graphEdgesById: new Map(),
    spawnedNodeMachines: new Map(),
    fullyExpandedEntityIds: new Set(),
    isEntityMapSizeOverProductLimit: false,
  }),
  initial: 'loading',
  states: {
    loading: {
      invoke: {
        id: 'loadInitialGraphActorFromLoadingState',
        input: ({context}) => ({entityId: context.entityId}),
        src: 'loadInitialGraph',
        onDone: {
          target: 'layouting',
          actions: [
            {
              type: 'mergeGraph',
              params: ({event}) => event.output,
            },
          ],
        },
        onError: {
          target: 'error',
        },
      },
    },
    layouting: {
      invoke: {
        id: 'layoutGraphActor',
        input: ({context}) => ({
          graphNodesById: context.graphNodesById,
          graphEdgesById: context.graphEdgesById,
          layoutSettings: context.layoutSettings,
        }),
        src: 'layoutGraph',
        onDone: {
          target: 'idle',
          actions: [assign(({event}) => event.output)],
        },
        onError: {
          target: 'error',
        },
      },
      on: {
        onNodesChange: {
          actions: ['onNodesChange'],
          target: 'debouncingLayout',
        },
        onEdgesChange: {
          actions: ['onEdgesChange'],
          target: 'debouncingLayout',
        },
        completedFetchingExtendedGraph: {
          target: 'debouncingLayout',
          actions: [
            {
              type: 'mergeGraph',
              params: ({event: {type, ...entityMapGraph}}) => entityMapGraph,
            },
          ],
        },
      },
    },
    idle: {
      on: {
        onNodesChange: {
          actions: ['onNodesChange'],
          target: 'debouncingLayout',
        },
        onEdgesChange: {
          actions: ['onEdgesChange'],
          target: 'debouncingLayout',
        },
        completedFetchingExtendedGraph: {
          target: 'debouncingLayout',
          actions: [
            {
              type: 'mergeGraph',
              params: ({event: {type, ...entityMapGraph}}) => entityMapGraph,
            },
          ],
        },
      },
    },
    debouncingLayout: {
      on: {
        onNodesChange: {
          actions: ['onNodesChange'],
          target: 'debouncingLayout',
          reenter: true,
        },
        onEdgesChange: {
          actions: ['onEdgesChange'],
          target: 'debouncingLayout',
          reenter: true,
        },
        completedFetchingExtendedGraph: {
          actions: [
            {
              type: 'mergeGraph',
              params: ({event: {type, ...entityMapGraph}}) => entityMapGraph,
            },
          ],
          target: 'debouncingLayout',
          reenter: true,
        },
      },
      after: {
        [DEFAULT_LAYOUT_DEBOUNCE_MS]: {
          target: 'layouting',
        },
      },
    },
    error: {},
  },
});
